import { sortBy } from 'lodash-es';
import type { Maybe } from '@soundxyz/utils';
import type { ChannelCuratorRole } from '@graphql/reactQuery';
import { ChannelCuratorsDocument, gql, useInfiniteQuery } from '@graphql/reactQuery';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ChannelCurators($channelId: UUID!, $pagination: CursorConnectionArgs!) {
    channelCurators(channelId: $channelId, pagination: $pagination) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          role
          user {
            id
            displayName
            publicAddress
            webappUri
            avatar {
              url
              id
            }
            artist {
              id
              name
              webappUri
            }
          }
        }
      }
    }
  }
`);

export function useChannelCurators({
  channelId,
  limit,
  sortByRole,
}: {
  channelId: Maybe<string>;
  limit: number;
  sortByRole: ChannelCuratorRole | false;
}) {
  const {
    orderedList: channelCurators,
    loadMoreNextPage: loadMore,
    isLoading,
    hasNextPage = false,
    refetch,
  } = useInfiniteQuery(ChannelCuratorsDocument, {
    filterQueryKey: {
      channelId,
      limit,
    },
    getNextPageParam({ data }) {
      return (
        data.channelCurators.pageInfo.hasNextPage && {
          after: data.channelCurators.pageInfo.endCursor,
        }
      );
    },
    enabled: !!channelId,
    variables: channelId
      ? ({ pageParam }) => {
          return {
            channelId,
            pagination: {
              first: limit,
              after: pageParam?.after || null,
            },
          };
        }
      : false,
    list({ channelCurators }) {
      const list = channelCurators.edges.map(edge => edge.node);
      if (sortByRole) return sortBy(list, curator => curator.role !== sortByRole);
      return list;
    },
    uniq({ id }) {
      return id;
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  return {
    channelCurators,
    loadMore,
    isLoading,
    hasNextPage,
    refetch,
  };
}
